import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Parameters, parseParameterValue, ParameterType } from '@communitly/shared';
import { isValidPrivacyType } from '@/utils/localeUtils';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';

export interface CommunityConfig {
  id: string;
  name: string;
  allowed_frontend_domain: string;
  description: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  logo_url?: string;
  default_category_group?: string;
  privacy_type: 'public' | 'private';
  default_locale: string;
  available_locales: string[];
  admin_contact_email?: string;
  community_app_url?: string;
  email_domain?: string;
  favicon_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  twitter_url?: string;
  country: string;
}

export const defaultCommunity: CommunityConfig = {
  id: '',
  name: '',
  allowed_frontend_domain: '',
  description: '',
  primary_color: '#000000',
  secondary_color: '#FFFFFF',
  accent_color: '#000000',
  privacy_type: 'public' as const,
  default_locale: 'en_GB',
  available_locales: ['en_GB'],
  default_category_group: undefined,
  community_app_url: undefined,
  email_domain: undefined,
  favicon_url: undefined,
  country: 'United Kingdom'
};

const REQUIRED_PARAMETERS = [
  'currency',
  'partial_discount_allowed',
  'display_contact_information',
  'default_category_group'
];

interface PublicCommunityContextType {
  // Community data
  currentCommunity: CommunityConfig;
  parameters: Parameters | null;
  getParameter: <T extends string | boolean | number>(key: string) => T | null;
  
  // Loading states
  isLoading: boolean;
  error: string | null;
  
  // Refresh methods
  refreshCommunityData: () => Promise<void>;
}

const PublicCommunityContext = createContext<PublicCommunityContextType>({
  currentCommunity: defaultCommunity,
  parameters: null,
  getParameter: () => null,
  isLoading: true,
  error: null,
  refreshCommunityData: async () => {},
});

export const usePublicCommunity = () => useContext(PublicCommunityContext);

// Loading spinner component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Community error component
const CommunityError = ({ errorMessage }: { errorMessage: string }) => (
  <div className="flex items-center justify-center min-h-screen p-4">
    <Alert variant="destructive" className="max-w-md">
      <AlertTitle>Community Error</AlertTitle>
      <AlertDescription>{errorMessage}</AlertDescription>
    </Alert>
  </div>
);

interface PublicCommunityProviderProps {
  children: ReactNode;
}

export const PublicCommunityProvider: React.FC<PublicCommunityProviderProps> = ({ children }) => {
  // State for community data
  const [currentCommunity, setCurrentCommunity] = useState<CommunityConfig>(defaultCommunity);
  const [parameters, setParameters] = useState<Parameters | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Note: Favicon is now handled server-side during SSR
  
  // Apply theme colors when community data changes
  useEffect(() => {
    if (currentCommunity.primary_color && !isLoading && !error) {
      const primaryHsl = hexToHSL(currentCommunity.primary_color);
      const secondaryHsl = hexToHSL(currentCommunity.secondary_color);
      const accentHsl = hexToHSL(currentCommunity.accent_color);
      
      if (primaryHsl) {
        document.documentElement.style.setProperty('--primary', `${primaryHsl.h} ${primaryHsl.s}% ${primaryHsl.l}%`);
      }
      
      if (secondaryHsl) {
        document.documentElement.style.setProperty('--secondary', `${secondaryHsl.h} ${secondaryHsl.s}% ${secondaryHsl.l}%`);
      }
      
      if (accentHsl) {
        document.documentElement.style.setProperty('--accent', `${accentHsl.h} ${accentHsl.s}% ${accentHsl.l}%`);
      }
    }
  }, [currentCommunity, isLoading, error]);
  
  // Load community data
  const loadCommunityData = async () => {
    console.log('[PublicCommunityContext] Loading community data');
    setIsLoading(true);
    setError(null);
    
    try {
      const currentDomain = window.location.hostname;
      console.log('[PublicCommunityContext] Current domain:', currentDomain);
      
      const data = await fetchCommunityWithRetry(currentDomain);
      
      if (data && data.length > 0) {
        const communityId = data[0].id;
        const privacyType = data[0].privacy_type || 'public';
        
        const validatedPrivacyType = isValidPrivacyType(privacyType) ? privacyType : 'public';
        

        const communityConfig: CommunityConfig = {
          id: communityId,
          name: data[0].name,
          allowed_frontend_domain: data[0].allowed_frontend_domain,
          description: data[0].description,
          primary_color: data[0].primary_color,
          secondary_color: data[0].secondary_color,
          accent_color: data[0].accent_color,
          logo_url: data[0].logo_url,
          default_category_group: data[0].default_category_group || undefined,
          privacy_type: validatedPrivacyType,
          default_locale: data[0].default_locale || 'en_GB',
          available_locales: data[0].available_locales || ['en_GB'],
          admin_contact_email: data[0].admin_contact_email,
          community_app_url: data[0].community_app_url,
          email_domain: data[0].email_domain,
          favicon_url: data[0].favicon_url,
          facebook_url: data[0].facebook_url,
          instagram_url: data[0].instagram_url,
          twitter_url: data[0].twitter_url,
          country: data[0].country || 'United Kingdom'
        };
        
        setCurrentCommunity(communityConfig);
        
        // Load parameters
        await loadParameters(communityId);
      } else {
        console.log('[PublicCommunityContext] No community found for domain:', currentDomain);
        setError(`No community found for this domain (${currentDomain}). Please contact your administrator.`);
      }
    } catch (error) {
      console.error('[PublicCommunityContext] Error loading community data:', error);
      setError(`Error loading community data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load parameters for a community
  const loadParameters = async (communityId: string) => {
    try {
      const { data: parameterData, error: paramError } = await supabase
        .from('parameters')
        .select('*')
        .eq('community_id', communityId);
        
      if (paramError) {
        console.error('[PublicCommunityContext] Error fetching parameters:', paramError);
        setError(`Error fetching community parameters: ${paramError.message}`);
        return;
      }
      
      if (parameterData && parameterData.length > 0) {
        const newParams: Parameters = {};
        
        parameterData.forEach((param: { key: string, value: string, type: string }) => {
          newParams[param.key] = {
            value: parseParameterValue(param.value, param.type as ParameterType),
            type: param.type as ParameterType
          };
        });
        
        const missingParams = REQUIRED_PARAMETERS.filter(key => !newParams[key]);
        
        if (missingParams.length > 0) {
          console.error('[PublicCommunityContext] Missing required parameters:', missingParams);
          setError(`Missing required community parameters: ${missingParams.join(', ')}`);
          return;
        }
        
        setParameters(newParams);
        console.log('[PublicCommunityContext] Parameters loaded:', newParams);
      }
    } catch (paramError) {
      console.error('[PublicCommunityContext] Exception fetching parameters:', paramError);
      setError(`Error loading community parameters: ${paramError instanceof Error ? paramError.message : 'Unknown error'}`);
    }
  };
  
  // Helper function to fetch community with retry
  const fetchCommunityWithRetry = async (domain: string, retries = 2) => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const query = supabase
          .from('communities')
          .select('*')
          .filter('allowed_frontend_domain', 'ilike', `%${domain}%`);
        
        const { data, error } = await query;
        
        if (error) {
          console.error(`[PublicCommunityContext] Error fetching community (attempt ${attempt + 1}):`, error);
          if (attempt === retries) throw error;
        } else {
          return data;
        }
      } catch (err) {
        console.error(`[PublicCommunityContext] Exception in fetch (attempt ${attempt + 1}):`, err);
        if (attempt === retries) throw err;
      }
      
      if (attempt < retries) {
        const delay = Math.pow(2, attempt) * 500;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    return null;
  };
  
  // Helper function to convert hex color to HSL
  function hexToHSL(hex: string): { h: number, s: number, l: number } | null {
    hex = hex.replace('#', '');
    
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    
    const l = (max + min) / 2;
    
    if (max === min) {
      return { h: 0, s: 0, l: Math.round(l * 100) };
    }
    
    const s = l > 0.5 ? (max - min) / (2 - max - min) : (max - min) / (max + min);
    
    let h;
    switch (max) {
      case r:
        h = ((g - b) / (max - min)) % 6;
        break;
      case g:
        h = (b - r) / (max - min) + 2;
        break;
      default:
        h = (r - g) / (max - min) + 4;
    }
    
    h = Math.round(h * 60);
    if (h < 0) h += 360;
    
    return { h, s: Math.round(s * 100), l: Math.round(l * 100) };
  }
  
  // Generic parameter getter function
  const getParameter = <T extends string | boolean | number>(key: string): T | null => {
    if (!parameters || !parameters[key]) {
      console.warn(`Parameter ${key} not found`);
      return null;
    }
    return parameters[key].value as T;
  };
  
  // Refresh community data (can be called when needed)
  const refreshCommunityData = async () => {
    await loadCommunityData();
  };
  
  useEffect(() => {
    // Initial data load
    loadCommunityData();
  }, []);
  
  // Show loading spinner while data is being fetched
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  // Show error message if there's an error
  if (error) {
    return <CommunityError errorMessage={error} />;
  }
  
  return (
    <PublicCommunityContext.Provider
      value={{
        currentCommunity,
        parameters,
        getParameter,
        isLoading,
        error,
        refreshCommunityData
      }}
    >
      {children}
    </PublicCommunityContext.Provider>
  );
};
